import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  FeedbackTicket, FeedbackTicketDetail, FeedbackMessage, SubmitFeedbackRequest
} from '../models/feedback.model';

const ME_BASE = `${environment.apiBaseUrl}/v1/admin/me/feedback`;

@Injectable({ providedIn: 'root' })
export class FeedbackService {
  private readonly http = inject(HttpClient);

  readonly submitting = signal(false);
  readonly uploading = signal(false);
  readonly myTickets = signal<FeedbackTicket[]>([]);
  readonly loadingTickets = signal(false);
  readonly screenshotEnabled = signal(false);
  readonly userUnreadCount = signal(0);

  loadConfig() {
    this.http.get<{ screenshot_upload_enabled: boolean }>(`${ME_BASE}/config`)
      .subscribe({ next: cfg => this.screenshotEnabled.set(cfg.screenshot_upload_enabled) });
  }

  submitTicket(req: SubmitFeedbackRequest) {
    return this.http.post<FeedbackTicket>(ME_BASE, req);
  }

  uploadScreenshot(file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<{ url: string }>(`${ME_BASE}/upload`, form);
  }

  loadMyTickets(includeClosed = false) {
    this.loadingTickets.set(true);
    return this.http
      .get<FeedbackTicket[]>(ME_BASE, {
        params: includeClosed ? { include_closed: 'true' } : {},
      })
      .subscribe({
        next: tickets => {
          this.myTickets.set(tickets);
          this.loadingTickets.set(false);
        },
        error: () => this.loadingTickets.set(false),
      });
  }

  getTicket(id: string) {
    return this.http.get<FeedbackTicketDetail>(`${ME_BASE}/${id}`);
  }

  addMessage(ticketId: string, body: string) {
    return this.http.post<FeedbackMessage>(
      `${ME_BASE}/${ticketId}/messages`,
      { body }
    );
  }

  loadUserUnreadCount() {
    this.http.get<{ count: number }>(`${ME_BASE}/unread-count`)
      .subscribe({ next: r => this.userUnreadCount.set(r.count) });
  }

  markUserRead(ticketId: string) {
    this.http.post<void>(`${ME_BASE}/${ticketId}/read`, {}).subscribe({
      next: () => {
        this.myTickets.update(ts => ts.map(t =>
          t.id === ticketId ? { ...t, user_unread: false } : t
        ));
        this.loadUserUnreadCount();
      }
    });
  }
}
